import appClient from '../../common/services/AppClient';
import {
    ApplicationDefinitionConditionalDto,
    InputFieldSettingDto
} from '../../iota_applications/types/ApplicationDefinition';
import {
    AdditionalClassificationDto,
    AdditionalClassificationOptionDto,
    AdditionalClassificationRefreshPreviewDto,
    ApplicationDefinitionConditionalPreviewBindingResponse
} from '../../application_definition_conditional/types';
import security from '../../common/services/SecurityService';
import { Utils } from '../../common/services/Utils';
import { RcFile } from 'antd/lib/upload';
import { endpoints } from '../../../config';
import { IotaTabExport } from '../types/IotaTabExport';
import { TabImportRequest, TabImportResponse } from '../types/IotaTabImport';
import { AdditionalClassificationVerification } from '../types/AdditionalClassificationVerification';
import { ImportWarningsResponse } from '../../common/services/ImportWarnings';

export default class ApplicationDefinitionConditionalService {
    exportTab(data: IotaTabExport, fileName: string) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        Utils.downloadBlob(blob, `${fileName.replace(/[<>:"/\\|?*]/g, '_')}.iota_tab`);
    }

    validateTab(projectId: string, appDefId: string, request: TabImportRequest) {
        return appClient.post<TabImportResponse>(
            `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/validate-tab`,
            request
        );
    }

    commitTab(projectId: string, appDefId: string, request: TabImportRequest) {
        return appClient.post<TabImportResponse>(
            `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/commit-tab`,
            request
        );
    }

    async updateData(
        projectId: string,
        appDefId: string,
        data: Pick<ApplicationDefinitionConditionalDto, 'conditions' | 'inputGroups'> & {
            additionalClassifications: AdditionalClassificationDto[];
            fieldSettings: InputFieldSettingDto[];
        }
    ) {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/application-definition-conditional`;

        return appClient.post<Omit<ApplicationDefinitionConditionalDto, 'type'>>(url, data);
    }

    async previewApplication(
        projectId: string,
        appDefId: string,
        data: Pick<ApplicationDefinitionConditionalDto, 'conditions' | 'inputGroups'> & { packageIds: string[] }
    ) {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/application-definition-conditional/preview-application`;

        return appClient.post<ApplicationDefinitionConditionalPreviewBindingResponse[]>(url, data);
    }

    async getAdditionalClassifications(projectId: string, appDefId: string) {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/application-definition-conditional/additional-classifications`;

        return appClient.get<AdditionalClassificationOptionDto[]>(url);
    }

    verifyAdditionalClassifications(projectId: string, appDefId: string, current: AdditionalClassificationDto[]) {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/application-definition-conditional/additional-classifications/verification`;
        return appClient.post<AdditionalClassificationVerification>(url, { current });
    }

    async getAdditionalClassificationPreview(projectId: string, appDefId: string, typeCode: string) {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/application-definition-conditional/additional-classifications/${encodeURIComponent(
            typeCode
        )}/preview`;

        return appClient.get<AdditionalClassificationDto>(url);
    }

    async getAdditionalClassificationRefreshPreview(
        projectId: string,
        appDefId: string,
        typeCode: string,
        current: AdditionalClassificationDto
    ) {
        const url = `${endpoints.manageUrl}projects/${projectId}/iota/applications/${appDefId}/application-definition-conditional/additional-classifications/${encodeURIComponent(
            typeCode
        )}/refresh-preview`;

        return appClient.post<AdditionalClassificationRefreshPreviewDto>(url, { current });
    }

    async exportAppConditions(id: string, projectId: string) {
        const mapForm = document.createElement('form');
        mapForm.setAttribute('id', 'iotaConditionsPostForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';
        const mapIotaIdInput = document.createElement('input');
        mapIotaIdInput.type = 'text';
        mapIotaIdInput.name = 'id';
        mapIotaIdInput.value = id;
        mapForm.appendChild(mapIotaIdInput);

        await security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/${id}/export-conditions${t}`;
            Utils.downloadFile(url, mapForm, 'iotaConditionsPostForm');
            return Promise.resolve();
        });
    }

    async commitAppBindings(id: string, projectId: string, file: RcFile) {
        var formData = new FormData();
        formData.append('file', file);
        const url = endpoints.manageUrl + `projects/${projectId}/iota/applications/${id}/commit-conditions`;
        return appClient.post<ImportWarningsResponse | undefined>(url, formData);
    }
}
